/*
 * Copyright © Lybe Sweden AB — read LICENSE_LYBE.txt
 */

type LogLevel = 'info' | 'debug' | 'warn' | 'error';

interface LogOptions {
    message: string;
    context?: Record<string, any>;
    level?: LogLevel;
    filename?: string;
    includeSystemInfo?: boolean;
}

export const logToFile = (input: string | LogOptions): void => {
    if (typeof window !== 'undefined') {
        // Client side - skip
        return;
    }

    const opts: LogOptions = typeof input === 'string' ? { message: input } : input;

    const {
        message,
        context = {},
        level = 'info',
        filename,
    } = opts;

    const fs = require('fs');
    const path = require('path');
    const os = require('os');

    try {
        const logsPath = 'var/logs';

        if (!fs.existsSync(logsPath)) {
            fs.mkdirSync(logsPath, { recursive: true });
        }

        const includeSystemInfo = opts.includeSystemInfo ?? false;

        const now = new Date();
        const isoTimestamp = now.toISOString();
        const humanTimestamp = new Intl.DateTimeFormat('sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZoneName: 'short'
        }).format(now);

        const logFileName = filename || `${level}.log`;
        const logFilePath = path.join(logsPath, logFileName);
        const lines: string[] = [];

        lines.push(`${isoTimestamp} [${level.toUpperCase()}]`);
        lines.push(`Date: ${humanTimestamp}`);
        lines.push(`Message: ${message}`);

        if (Object.keys(context).length > 0) {
            lines.push(`Context: ${JSON.stringify(context, null, 2)}`);
        }

        function formatBytes(bytes: number): string {
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
            if (bytes === 0) return '0 Bytes';
            const i = Math.floor(Math.log(bytes) / Math.log(1024));
            return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
        }

        function formatUptime(seconds: number): string {
            const hrs = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            const secs = Math.floor(seconds % 60);
            return `${hrs}h ${mins}m ${secs}s`;
        }

        if (includeSystemInfo) {
            const systemInfoLines = [
                `Node Version: ${process.version}`,
                `Hostname: ${os.hostname()}`,
                `Memory: Free ${formatBytes(os.freemem())} / Total ${formatBytes(os.totalmem())}`,
                `Uptime: ${formatUptime(os.uptime())}`,
                `Environment: NODE_ENV=${process.env.NODE_ENV || 'unknown'}${process.env.PORT ? ', PORT=' + process.env.PORT : ''}`
            ];

            lines.push('System Info:\n' + systemInfoLines.map(line => '  ' + line).join('\n'));
        }

        const logEntry = lines.join('\n') + '\n\n';
        fs.appendFileSync(logFilePath, logEntry);

    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to write log file:', e);
    }
};
